import { useState } from "react";
import { SlidersHorizontal, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { fdate, fmt, num, run } from "../lib/util";
import { Alert, Badge, Btn, Field, Input, SectionTitle, Table, Empty } from "../components/kit";
import { useSchool } from "./ctx";
import { Downloads } from "./Forms";
import { OPENING } from "./Overview";

function OverrideDialog({ row, onClose }) {
  const { month, bump } = useSchool();
  const [f, setF] = useState({ final_qty: row.final < 0 ? 0 : row.final, reason: "", confirmed: false });
  const save = async () => {
    if (await run(() => api.post("/s/overrides", { month, product_id: row.product_id, final_qty: num(f.final_qty), reason: f.reason, confirmed: f.confirmed }), "Ajuste registrado — meses seguintes recalculados")) {
      bump();
      onClose();
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader><DialogTitle>Ajustar saldo final · {row.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Saldo calculado: <b>{fmt(row.final)} {row.unit}</b>. O ajuste confirmado passa a ser o saldo final deste mês e a base do mês seguinte.</p>
        <Field label="Saldo final correto"><Input data-testid="override-qty-input" type="number" step="any" value={f.final_qty} onChange={(e) => setF({ ...f, final_qty: e.target.value })} /></Field>
        <Field label="Motivo (obrigatório)"><Input data-testid="override-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <label className="flex gap-2 text-sm"><input type="checkbox" data-testid="override-confirm-checkbox" checked={f.confirmed} onChange={(e) => setF({ ...f, confirmed: e.target.checked })} />Confirmo explicitamente este ajuste manual</label>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="override-save-button" onClick={save}>Registrar ajuste</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Leftover({ r, month, bump }) {
  const [v, setV] = useState(r.leftover ?? "");
  const save = async () => {
    const q = v === "" ? null : num(v);
    if (q === (r.leftover ?? null)) return;
    if (await run(() => api.put("/s/leftovers", { month, product_id: r.product_id, qty: q }), q == null ? "Sobra apagada" : "Sobra registrada — vira o estoque anterior do mês seguinte")) bump();
  };
  return (
    <div>
      <Input data-testid={`leftover-${r.product_id}`} type="number" step="any" min="0" className="w-24 text-right py-1" placeholder="—" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
      {r.leftover != null && r.leftover_kept < r.leftover && <div className="text-[11px] text-amber-700">só há {fmt(r.leftover_kept)}</div>}
    </div>
  );
}

export default function MonthlyStock() {
  const { stock, bump } = useSchool();
  const [ov, setOv] = useState(null);
  if (!stock) return null;
  const removeOv = async (r) => {
    if (window.confirm("Remover o ajuste manual e voltar ao saldo calculado?") && (await run(() => api.delete(`/s/overrides/${r.override.id}`), "Ajuste removido"))) bump();
  };
  return (
    <div className="space-y-4">
      <SectionTitle title="Controle mensal de estoque" subtitle="Saldo final = saldo inicial + entradas − saídas. Recalculado a cada edição, inclusive nos meses seguintes.">
        <Downloads form="controle_mensal" label="controle mensal" />
      </SectionTitle>
      <div className="flex flex-wrap gap-2">
        <Badge tone={OPENING[stock.opening_status][1]}>{OPENING[stock.opening_status][0]}</Badge>
        {stock.start_month && stock.month < stock.start_month && <Badge tone="amber">Mês anterior à implantação — saldo inicial não controlado</Badge>}
      </div>
      {stock.negatives.length > 0 && <Alert tone="red" title="Estoque negativo — corrija para liberar a exportação" testid="monthly-negatives">{stock.negatives.map((n) => <div key={n.product_id}>{n.name}: {fmt(n.final)}</div>)}</Alert>}
      <Table testid="monthly-table">
        <thead><tr><th>Gênero</th><th>Un.</th><th className="num">Saldo inicial</th><th className="num">Entradas</th><th className="num">Saídas est. (A)</th><th className="num">Saídas reais (B)</th><th className="num">Saídas usadas</th><th className="num" title="Opcional: quantidade que sobrou e passa para o mês seguinte">Sobra para o mês seguinte</th><th className="num">Saldo final</th><th /></tr></thead>
        <tbody>
          {stock.rows.map((r) => (
            <tr key={r.product_id} data-testid={`monthly-row-${r.product_id}`} className={r.final < 0 ? "!bg-red-50" : ""}>
              <td className="font-semibold">{r.name}</td>
              <td>{r.unit}</td>
              <td className="num">{fmt(r.opening)}</td>
              <td className="num">{fmt(r.entradas)}</td>
              <td className={`num ${stock.base === "estimativa" ? "" : "text-slate-400"}`}>{fmt(r.saidas_est)}</td>
              <td className={`num ${stock.base === "real" ? "" : "text-slate-400"}`}>{fmt(r.saidas_real)}</td>
              <td className="num font-semibold">{fmt(r.saidas)}</td>
              <td className="num"><Leftover key={`${stock.month}-${r.leftover}`} r={r} month={stock.month} bump={bump} /></td>
              <td className={`num font-bold ${r.final < 0 ? "text-red-700" : "text-[#0B192C]"}`}>{fmt(r.final)}{r.override && <div title={r.override.reason}><Badge tone="orange">ajuste manual</Badge></div>}</td>
              <td className="whitespace-nowrap">
                {r.override ? <Btn variant="ghost" title="Remover ajuste" onClick={() => removeOv(r)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
                  : <Btn variant="ghost" data-testid={`override-${r.product_id}`} title="Ajustar saldo" onClick={() => setOv(r)}><SlidersHorizontal className="w-4 h-4" /></Btn>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {!stock.rows.length && <Empty>Nenhum gênero movimentado neste mês.</Empty>}
      {stock.lots.length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-[#0B192C] mt-6 mb-2">Lotes com saldo e disponibilidade</h3>
          <p className="text-xs text-slate-500 mb-2">Cada nota é distribuída nos dias de merenda a partir da emissão até a próxima nota do mesmo gênero. As notas da agricultura familiar valem desde o primeiro dia letivo do mês da nota, porque as entregas acontecem ao longo do mês. Tudo é consumido dentro do mês (estoque final zero).</p>
          <Table testid="lots-table">
            <thead><tr><th>Produto</th><th>Origem</th><th className="num">Qtd. do lote</th><th className="num">Saldo do lote</th><th>Disponível desde</th><th>Regra da semana</th></tr></thead>
            <tbody>
              {stock.lots.map((l, i) => (
                <tr key={i}>
                  <td>{l.product}</td>
                  <td className="text-xs">{l.source.kind === "estoque_inicial" ? "Estoque inicial" : l.source.kind === "ajuste" ? "Ajuste manual" : `NF ${l.source.number || ""} · ${l.source.supplier || ""} · emissão ${fdate(l.source.issue_date)}`}</td>
                  <td className="num">{fmt(l.qty)}</td>
                  <td className="num font-semibold">{fmt(l.remaining)}</td>
                  <td>{fdate(l.available_from)}</td>
                  <td className="text-xs">{l.pct != null ? `NF de sexta: 70% na semana, 30% para a semana seguinte` : "distribuído nos dias de merenda"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {ov && <OverrideDialog row={ov} onClose={() => setOv(null)} />}
    </div>
  );
}
